import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import httpService from './httpService'
import { saveBlob } from '@/utils/csvTemplate'
import { Response } from '@/types'

/**
 * Client mirror of the club-facing benefits API.
 *
 * Two distinct permissions sit behind these calls, and the UI has to respect the split: seeing the
 * list and marking someone redeemed is front-desk work that comes with the director/organizer
 * roles, while changing what the club offers is a commercial decision only a self-managing club may
 * make. A club that is not whitelisted can still work its list.
 */

export interface ClubBenefitAssignment {
  grantId: number
  /** Already redacted server-side for members who asked for that — never reconstruct a name. */
  name: string
  duprId: string | null
  /**
   * The short code the member is showing on their phone, e.g. `K7M3-QX9P`.
   *
   * The same string on both sides of the counter — the server derives it identically for the
   * member's screen and for this row — so staff can find a claim by what they were read out rather
   * than by spelling a surname.
   *
   * Optional only because a deploy can be talking to an API from before it existed.
   */
  claimReference?: string
  benefitUrn: string
  /** From the version the member PINNED, not whatever the definition says today. */
  label: string | null
  claimedAt: string
  redeemedAt: string | null
}

export interface ClubBenefitOffer {
  id: number
  /**
   * A plain string. `BenefitUrn` is `@JsonValue`-serialised server-side, so it arrives as
   * `"urn:dupr:benefit:club:credit"` and never as an object — typing it as one compiled fine and
   * made every read of it `undefined` at runtime.
   */
  bundleUrn: string
  /** What to call it. Resolved server-side from the definition version in force. */
  label: string
  created: string
}

/**
 * A canned eligibility rule the club can select. Directors are not rule authors, so the club picks
 * one of these and fills in its typed parameters; the server compiles the choice into an ordinary
 * club-scoped targeting rule.
 */
export interface BenefitEligibilityTemplate {
  key: string
  label: string
  description: string
  parameters: {
    key: string
    label: string
    /**
     * Discriminated on `kind` — only `int` is emitted by the current templates. `min`/`max` are
     * `| null` rather than optional because the server always sends them: they are nullable Kotlin
     * fields, so "no bound" arrives as an explicit null rather than an absent key.
     */
    type: { kind: string; min: number | null; max: number | null }
    required: boolean
    description: string | null
  }[]
}

export interface ClubBenefitEligibilitySelection {
  bundleUrn: string
  templateKey: string
  parameters: Record<string, string>
}

/**
 * The `Result` envelope these endpoints return.
 *
 * `results` is not optional: the server's field is a non-null list, so an empty answer arrives as
 * `[]`. Typing it as possibly-absent made every caller write a `?? []` that could never fire, and
 * hid the one case that matters — a *failed* request, which axios rejects long before this shape is
 * reached.
 */
type ResultsOf<T> = Response & { results: T[] }

export const ClubBenefitQueryKeys = {
  assignments: (clubId: number) => ['club-benefit-assignments', clubId] as const,
  offers: (clubId: number) => ['club-benefit-offers', clubId] as const,
  eligibility: (clubId: number) => ['club-benefit-eligibility', clubId] as const,
  eligibilityTemplates: (clubId: number) => ['club-benefit-eligibility-templates', clubId] as const,
}

const base = (clubId: number) => `/club/${clubId}/benefits/v1.0`

/** How many claims to fetch per request. Matches the server's own default page size. */
const ASSIGNMENTS_PAGE_SIZE = 50

/**
 * The club's claim list.
 *
 * Paged: a busy club accumulates claims indefinitely, and a list that silently stopped at the
 * server's default would read as "nobody else has claimed" — which is exactly the sentence a front
 * desk would act on.
 */
export const useClubBenefitAssignments = (clubId: number, enabled = true) =>
  useInfiniteQuery({
    queryKey: ClubBenefitQueryKeys.assignments(clubId),
    enabled,
    // No `initialPageParam` — this repo is on React Query v4, where the first page simply runs with
    // `pageParam` undefined.
    queryFn: async ({ signal, pageParam = 0 }): Promise<ClubBenefitAssignment[]> => {
      const res = await httpService.Get<ResultsOf<ClubBenefitAssignment>>(
        `${base(clubId)}/assignments?offset=${pageParam}&limit=${ASSIGNMENTS_PAGE_SIZE}`,
        { signal }
      )
      return res.results
    },
    /**
     * The next offset counts DB rows requested, not rows received.
     *
     * Two things make `allPages.flat().length` wrong here. The service drops rows it cannot resolve
     * (`mapNotNull` over a missing grant or user), so a full 50-row database page can arrive with
     * 49 items — and counting items would then re-request one already shown, for ever. And the
     * query is `order by id desc`, so newly claimed benefits enter at the *front*: paging by a
     * count that shrinks or shifts double-shows rows.
     *
     * Counting pages instead keeps the offset aligned with what was asked for. It does not fix the
     * front-insertion skew — a claim arriving mid-scroll can still repeat one row — which is a
     * cursor's job, and worth doing if this list ever grows a keyset.
     */
    getNextPageParam: (lastPage: ClubBenefitAssignment[], allPages: ClubBenefitAssignment[][]) =>
      // A page shorter than requested can still be the end; only an EMPTY page proves it, because
      // a short page may just be one the service could not fully resolve.
      lastPage.length === 0 ? undefined : allPages.length * ASSIGNMENTS_PAGE_SIZE,
  })

/**
 * What this club offers right now.
 *
 * Read-only from the club's side: DUPR operations author the set centrally, so there is no write
 * companion here. It feeds the eligibility picker, which renders one rule row per offered benefit.
 */
export const useClubBenefitOffers = (clubId: number, enabled = true) =>
  useQuery({
    queryKey: ClubBenefitQueryKeys.offers(clubId),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await httpService.Get<ResultsOf<ClubBenefitOffer>>(`${base(clubId)}/offers`, {
        signal,
      })
      return res.results
    },
  })

export const useMarkBenefitRedeemed = (clubId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (grantId: number) => {
      const res = await httpService.Post<ResultsOf<ClubBenefitAssignment>>(`${base(clubId)}/${grantId}/redeem`, {
        capacity: 'CLUB_STAFF',
      })
      return res.results
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ClubBenefitQueryKeys.assignments(clubId) })
    },
  })
}

export const useBenefitEligibilityTemplates = (clubId: number, enabled = true) =>
  useQuery({
    queryKey: ClubBenefitQueryKeys.eligibilityTemplates(clubId),
    enabled,
    // The catalog is DI-discovered server-side and changes only on deploy, so it is worth holding
    // rather than refetching every time the tab is opened.
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const res = await httpService.Get<
        ResultsOf<{ templates: BenefitEligibilityTemplate[]; selfManage: boolean; canEdit: boolean }>
      >(`${base(clubId)}/eligibility/templates`, { signal })
      // The catalog is identical for every club; `selfManage` is what says whether this one may
      // apply it. Without that the UI cannot tell "nothing to pick" from "not your call".
      return res.results[0] ?? { templates: [], selfManage: false, canEdit: false }
    },
  })

export const useClubBenefitEligibility = (clubId: number, enabled = true) =>
  useQuery({
    queryKey: ClubBenefitQueryKeys.eligibility(clubId),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await httpService.Get<ResultsOf<{ selections: ClubBenefitEligibilitySelection[] }>>(
        `${base(clubId)}/eligibility`,
        { signal }
      )
      return res.results[0]?.selections ?? []
    },
  })

/**
 * Set — or clear — who this club accepts a benefit from.
 *
 * Clearing is a delete rather than selecting an "everyone" template, because the two differ: no rule
 * means any holder may claim here, while the all-members template still requires club membership.
 */
export const useSetClubBenefitEligibility = (clubId: number) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (selection: ClubBenefitEligibilitySelection | { bundleUrn: string; templateKey: null }) => {
      if (selection.templateKey === null) {
        await httpService.Delete<Response>(
          `${base(clubId)}/eligibility?bundleUrn=${encodeURIComponent(selection.bundleUrn)}`
        )
        return
      }
      await httpService.Put(`${base(clubId)}/eligibility`, selection)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ClubBenefitQueryKeys.eligibility(clubId) })
    },
  })
}

/**
 * The whole claim list as a CSV, downloaded straight away.
 *
 * Fetched as a blob rather than linked to, because the endpoint is authenticated: an `href` carries
 * no bearer token, so a plain link would download the login page's error body under a .csv name.
 *
 * Names in the file are initialled, unlike the screen. That is the server's decision and worth
 * knowing when reading one: a downloaded file cannot be recalled, so it carries only enough to
 * recognize a member at the desk.
 */
export const useDownloadClubClaimsCsv = (clubId: number) =>
  useMutation({
    mutationFn: async () => {
      const blob = await httpService.Get<Blob>(`${base(clubId)}/assignments.csv`, {
        responseType: 'blob',
        // The client's default `Accept: application/json` doesn't describe a file. JSON stays
        // acceptable because a refusal still answers with a JSON body.
        headers: { Accept: 'text/csv, application/json' },
      })
      saveBlob(blob, `benefit-claims-${clubId}.csv`)
    },
  })

/** Where an asynchronous export has got to. `RUNNING` covers "queued" — Quartz has not started it yet. */
export interface ClubExportRun {
  runId: string
  status: 'RUNNING' | 'COMPLETED' | 'FAILED'
}

/**
 * The same list, built in the background.
 *
 * Worth having alongside the direct download because the direct one holds a connection open for as
 * long as the file takes to build, and a club with years of history will lose that connection to a
 * load-balancer idle timeout before it finishes. This one returns immediately with a run to poll.
 *
 * Polling is driven by the caller re-invoking [pollExport] rather than by a React Query interval,
 * because the terminal state is a *download*, not a value to cache: once the run completes the same
 * URL streams the file instead of JSON, and a cached query that had already stored the JSON status
 * would never ask again.
 */
export const useClubClaimsExport = (clubId: number) => {
  const exportUrl = (runId: string) => `${base(clubId)}/assignments/export/${runId}`

  const start = async (): Promise<string> => {
    const res = await httpService.Post<ResultsOf<ClubExportRun>>(`${base(clubId)}/assignments/export`, {})
    const runId = res.results[0]?.runId
    if (!runId) throw new Error('The export started but did not say which run to follow')
    return runId
  }

  /**
   * One poll. Resolves to the run's status, and to `COMPLETED` only once the file has been saved.
   *
   * The response is read as a blob whatever it is, because the endpoint answers JSON while the run
   * is in flight and CSV once it is done — asking for JSON would reject the very response that
   * means success. The content type is what tells the two apart.
   */
  const poll = async (runId: string): Promise<ClubExportRun['status']> => {
    const response = await httpService.GetRaw<Blob>(exportUrl(runId), { responseType: 'blob' })
    const contentType = String(response.headers['content-type'] ?? '')
    if (contentType.includes('text/csv')) {
      saveBlob(response.data, `benefit-claims-${clubId}-${runId}.csv`)
      return 'COMPLETED'
    }
    const status = JSON.parse(await response.data.text()) as ClubExportRun
    return status.status ?? 'RUNNING'
  }

  return { start, poll, exportUrl }
}
